import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'hotel/navigation';

import { PrimaryButton } from 'components/Button';
import TripCard from 'components/TripCard';
import TripTabs from 'components/TripTabs';
import {
  useClientSelfBookingsQuery,
  useClientGroupRequestsQuery,
  TripStatus,
} from 'generated/graphql';

import TripsPageLayout from './TripsPageLayout';

const TRIPS_PER_PAGE = 10;

// Status mapping between URL and GraphQL enum for self-bookings
const SELF_BOOKING_STATUS_MAPPING: Record<string, TripStatus | undefined> = {
  upcoming: TripStatus.Upcoming,
  'in-house': TripStatus.InHouse,
  pending: TripStatus.Pending,
  past: TripStatus.Past,
  all: undefined, // undefined means no filter
};

// Status mapping for group requests
const GROUP_REQUEST_STATUS_MAPPING: Record<string, TripStatus | undefined> = {
  'request-received': TripStatus.RequestReceived,
  'in-progress': TripStatus.InProgress,
  'more-info-required': TripStatus.MoreInfoRequired,
  'awaiting-confirmation': TripStatus.AwaitingConfirmation,
  confirmed: TripStatus.Confirmed,
  canceled: TripStatus.Canceled,
  all: undefined, // undefined means no filter
};

const Trips: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract trip type and status from URL (/trips?type=selfBookings&status=upcoming)
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const urlTripType = urlParams.get('type') || 'selfBookings';
  const urlStatus = urlParams.get('status') || 'all';

  const [activeTripType, setActiveTripType] = useState<
    'selfBookings' | 'groupRequests'
  >(urlTripType === 'groupRequests' ? 'groupRequests' : 'selfBookings');
  const [selectedStatus, setSelectedStatus] = useState(urlStatus);

  // Separate pagination state for each trip type
  const [selfBookingPage, setSelfBookingPage] = useState(0);
  const [groupRequestPage, setGroupRequestPage] = useState(0);

  // Convert selected status to GraphQL enum based on trip type
  const currentStatusMapping =
    activeTripType === 'selfBookings'
      ? SELF_BOOKING_STATUS_MAPPING
      : GROUP_REQUEST_STATUS_MAPPING;
  const graphqlStatus = currentStatusMapping[selectedStatus];

  // Always fetch self-bookings data (for counts and when active)
  const selfBookingsQuery = useClientSelfBookingsQuery({
    variables: {
      limit: TRIPS_PER_PAGE,
      offset: selfBookingPage * TRIPS_PER_PAGE,
      status: activeTripType === 'selfBookings' ? graphqlStatus : undefined,
    },
    errorPolicy: 'all',
    // Apollo Client will cache results and avoid unnecessary network requests
    notifyOnNetworkStatusChange: true,
  });

  // Always fetch group requests data (for counts and when active)
  const groupRequestsQuery = useClientGroupRequestsQuery({
    variables: {
      limit: TRIPS_PER_PAGE,
      offset: groupRequestPage * TRIPS_PER_PAGE,
      status: activeTripType === 'groupRequests' ? graphqlStatus : undefined,
    },
    errorPolicy: 'all',
    // Apollo Client will cache results and avoid unnecessary network requests
    notifyOnNetworkStatusChange: true,
  });

  // Get current data based on active trip type
  const currentQuery =
    activeTripType === 'selfBookings' ? selfBookingsQuery : groupRequestsQuery;
  const currentPage =
    activeTripType === 'selfBookings' ? selfBookingPage : groupRequestPage;
  const setCurrentPage =
    activeTripType === 'selfBookings'
      ? setSelfBookingPage
      : setGroupRequestPage;

  const trips =
    activeTripType === 'selfBookings'
      ? selfBookingsQuery.data?.clientSelfBookings?.items || []
      : groupRequestsQuery.data?.clientGroupRequests?.items || [];
  const totalCount =
    activeTripType === 'selfBookings'
      ? selfBookingsQuery.data?.clientSelfBookings?.total || 0
      : groupRequestsQuery.data?.clientGroupRequests?.total || 0;
  const hasNextPage =
    activeTripType === 'selfBookings'
      ? selfBookingsQuery.data?.clientSelfBookings?.hasNextPage || false
      : groupRequestsQuery.data?.clientGroupRequests?.hasNextPage || false;
  const hasPreviousPage =
    activeTripType === 'selfBookings'
      ? selfBookingsQuery.data?.clientSelfBookings?.hasPreviousPage || false
      : groupRequestsQuery.data?.clientGroupRequests?.hasPreviousPage || false;

  // Status counts for the tabs - always available for both trip types
  const selfBookingCounts = selfBookingsQuery.data?.clientSelfBookings
    ?.statusCounts || {
    all: 0,
    inHouse: 0,
    upcoming: 0,
    pending: 0,
    past: 0,
  };

  const groupRequestCounts = groupRequestsQuery.data?.clientGroupRequests
    ?.statusCounts || {
    all: 0,
    requestReceived: 0,
    inProgress: 0,
    moreInfoRequired: 0,
    awaitingConfirmation: 0,
    confirmed: 0,
    canceled: 0,
  };

  // Check if counts are loading for tab display
  const countsLoading = selfBookingsQuery.loading || groupRequestsQuery.loading;

  // Update URL and state when trip type or status changes
  useEffect(() => {
    const newUrlTripType = urlParams.get('type') || 'selfBookings';
    const newUrlStatus = urlParams.get('status') || 'all';

    if (newUrlTripType !== activeTripType) {
      setActiveTripType(
        newUrlTripType === 'groupRequests' ? 'groupRequests' : 'selfBookings',
      );
    }

    if (newUrlStatus !== selectedStatus) {
      setSelectedStatus(newUrlStatus);
    }
  }, [location.search, activeTripType, selectedStatus, urlParams]);

  const handleTripTypeChange = (tripType: 'selfBookings' | 'groupRequests') => {
    setActiveTripType(tripType);
    setSelectedStatus('all'); // Reset status when changing trip type

    // Update URL
    const newParams = new URLSearchParams(location.search);
    newParams.set('type', tripType);
    newParams.set('status', 'all');
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(0); // Reset pagination when filtering

    // Update URL
    const newParams = new URLSearchParams(location.search);
    newParams.set('status', status);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  const handleRetry = () => {
    currentQuery.refetch();
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => Math.max(0, prev - 1));
    }
  };

  // Loading state
  if (currentQuery.loading) {
    return (
      <TripsPageLayout>
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Trips</h1>
            <p className="mt-2 text-gray-600">
              View and manage your hotel reservations and group requests
            </p>
          </div>
          <PrimaryButton onClick={() => navigate('/')}>
            Start a trip
          </PrimaryButton>
        </div>

        {/* Loading skeleton */}
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse rounded-lg border border-gray-200 bg-white p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-4 w-1/4 rounded bg-gray-200"></div>
                  <div className="h-6 w-1/2 rounded bg-gray-200"></div>
                  <div className="h-4 w-1/3 rounded bg-gray-200"></div>
                </div>
                <div className="h-6 w-16 rounded bg-gray-200"></div>
              </div>
            </div>
          ))}
        </div>
      </TripsPageLayout>
    );
  }

  // Error state
  if (currentQuery.error) {
    return (
      <TripsPageLayout>
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Trips</h1>
            <p className="mt-2 text-gray-600">
              View and manage your hotel reservations and group requests
            </p>
          </div>
          <PrimaryButton onClick={() => navigate('/')}>
            Start a trip
          </PrimaryButton>
        </div>

        {/* Error state */}
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-start">
            <div className="shrink-0">
              <svg
                className="size-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Failed to load trips
              </h3>
              <p className="mt-1 text-sm text-red-700">
                There was an error loading your trips. Please try again.
              </p>
              <div className="mt-4">
                <button
                  onClick={handleRetry}
                  className="rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </TripsPageLayout>
    );
  }

  // Empty state
  if (!currentQuery.loading && trips.length === 0) {
    const tripTypeLabel =
      activeTripType === 'selfBookings' ? 'self-bookings' : 'group requests';
    const statusLabel =
      selectedStatus === 'all' ? '' : ` ${selectedStatus.replace('-', ' ')}`;

    return (
      <TripsPageLayout>
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Trips</h1>
            <p className="mt-2 text-gray-600">
              View and manage your hotel reservations and group requests
            </p>
          </div>
          <PrimaryButton onClick={() => navigate('/')}>
            Start a trip
          </PrimaryButton>
        </div>

        {/* Tab Navigation */}
        <TripTabs
          activeTripType={activeTripType}
          onTripTypeChange={handleTripTypeChange}
          activeStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          selfBookingCounts={selfBookingCounts}
          groupRequestCounts={groupRequestCounts}
          loading={countsLoading}
        />

        {/* Empty state */}
        <div className="mt-6 text-center">
          <div className="mx-auto size-24 text-gray-400">
            <svg
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="size-24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h3 className="mt-6 text-lg font-medium text-gray-900">
            No {tripTypeLabel} found
          </h3>
          <p className="mt-2 text-gray-500">
            You don&apos;t have any{statusLabel} {tripTypeLabel} yet.
          </p>
          <div className="mt-6">
            <a
              href="/hotel-search"
              className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Search Hotels
            </a>
          </div>
        </div>
      </TripsPageLayout>
    );
  }

  // Success state with trips
  return (
    <TripsPageLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Trips</h1>
          <p className="mt-2 text-gray-600">
            View and manage your hotel reservations and group requests
          </p>
          {totalCount > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {totalCount} {totalCount === 1 ? 'trip' : 'trips'}
              {selectedStatus !== 'all' &&
                ` in ${selectedStatus.replace('-', ' ')}`}
            </p>
          )}
        </div>
        <PrimaryButton onClick={() => navigate('/')}>
          Start a trip
        </PrimaryButton>
      </div>

      {/* Tab Navigation */}
      <TripTabs
        activeTripType={activeTripType}
        onTripTypeChange={handleTripTypeChange}
        activeStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selfBookingCounts={selfBookingCounts}
        groupRequestCounts={groupRequestCounts}
        loading={countsLoading}
      />

      {/* Trips List */}
      <div className="mt-6 space-y-4">
        {trips.map((trip) => (
          <TripCard key={trip.id} trip={trip} />
        ))}
      </div>

      {/* Pagination */}
      {(hasNextPage || hasPreviousPage) && (
        <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage}
              className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white p-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="sr-only">Previous</span>
              <svg className="size-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white p-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="sr-only">Next</span>
              <svg className="size-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {currentPage * TRIPS_PER_PAGE + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min((currentPage + 1) * TRIPS_PER_PAGE, totalCount)}
                </span>{' '}
                of <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                  className="relative inline-flex items-center rounded-l-md p-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="size-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  className="relative inline-flex items-center rounded-r-md p-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="size-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </TripsPageLayout>
  );
};

export default Trips;
